import axios from 'axios'
import { db_login } from '@/database'
import store from '@/store'
import router from '@/router'
import { isSsoLogoutRedirect } from '@/utils/ssoLogout'

let API_URL = process.env.VUE_APP_API_URL

/**
 * Extrae el nombre de fichero de la cabecera Content-Disposition.
 *
 * @param {string|undefined} header
 * @returns {string|null}
 */
function parseContentDispositionFilename(header) {
    if (!header) {
        return null
    }

    const utf8Match = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header)
    if (utf8Match && utf8Match[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''))
        } catch (error) {
            return utf8Match[1].trim().replace(/^"|"$/g, '')
        }
    }

    const asciiMatch = /filename="?([^";]+)"?/i.exec(header)
    if (asciiMatch && asciiMatch[1]) {
        return asciiMatch[1].trim()
    }

    return null
}

async function handleUnauthorizedSession() {
    const result = await store.dispatch('login/logout')
    if (isSsoLogoutRedirect(result)) {
        return false
    }
    if (process.env.VUE_APP_IS_APP === 'TRUE') {
        return router.push('/login').then(function () {
            return false
        })
    }
    window.location.href =
        window.window.location.origin +
        (window.location.hash == '' ? '' : window.location.pathname.substring(0, window.location.pathname.length - 1)) +
        (process.env.VUE_APP_URL_TYPE == 'HASH' ? '/#' : '') +
        '/logout'
    return false
}

// ADD BY DEFAULT TIMEOUT 30sec
const timeout = 30000
axios.defaults.timeout = timeout

export class Api {
    constructor() {}

    async get(uri, options = {}) {
        this.setEnviromentUrl()

        let token = store.getters['login/getToken']
        if (!token) {
            token = await db_login.getItem('token')
        }
        axios.defaults.headers.common['Authorization'] = token

        const url = API_URL + uri
        return axios
            .get(url, {
                withCredentials: true,
                timeout: options.timeout || timeout,
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    if ([503].includes(status) && process.env.VUE_APP_IS_APP !== 'TRUE') {
                        localStorage.setItem('maintenance', true)
                        store.commit('setMaintenance', true)
                        return false
                    }
                    if (status !== 200) {
                        return false
                    }
                    store.commit('setMaintenance', false)
                    localStorage.setItem('maintenance', false)
                    return true
                }
            })
            .then(function (response) {
                return response.data
            })
            .catch((error) => {
                // TODO: validate more error messages
                // Si el error es por status code no válido, lanzar excepción para que entre en catch de tasks.js
                if (error.response && error.response.status && error.response.status !== 200) {
                    return Promise.reject({ status: false, msg: 'Error: Api Error' })
                }
                return { status: false, msg: 'Error: Network Error' + error }
            })
    }

    async post(uri, params, options = {}) {
        this.setEnviromentUrl()

        let token = store.getters['login/getToken']
        if (!token) {
            token = await db_login.getItem('token')
        }

        axios.defaults.headers.common['Authorization'] = token

        const oParams = new URLSearchParams()
        Object.keys(params).forEach((key) => {
            oParams.append(key, params[key])
        })

        const url = API_URL + uri
        const appVersion = process.env.VUE_APP_VERSION
        const platform = process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2
        const coords = localStorage.device_coords ? localStorage.device_coords : false
        const uuid = localStorage.device_uuid ? localStorage.device_uuid : false

        if (!params.version) oParams.append('version', appVersion)
        if (!params.platform) oParams.append('platform', platform)
        if (coords) oParams.append('geolocation', JSON.parse(coords).latitude + ',' + JSON.parse(coords).longitude)
        if (uuid) oParams.append('uuid', uuid)

        return axios
            .post(url, oParams, {
                withCredentials: true,
                timeout: options.timeout || timeout,
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    if ([503].includes(status) && process.env.VUE_APP_IS_APP !== 'TRUE') {
                        store.commit('setMaintenance', true)
                        localStorage.setItem('maintenance', true)
                        return false
                    }
                    if (status !== 200) {
                        return false
                    }
                    store.commit('setMaintenance', false)
                    localStorage.setItem('maintenance', false)
                    return true
                }
            })
            .then(function (response) {
                return response.data
            })
            .catch((error) => {
                // TODO: validate more error messages
                // Si el error es por status code no válido, lanzar excepción para que entre en catch de tasks.js
                if (error.response && error.response.status && error.response.status !== 200) {
                    return Promise.reject({
                        status: false,
                        msg: 'Error: Api Error',
                        httpStatus: error.response.status
                    })
                }
                return { status: false, msg: 'Error: Network Error' + error }
            })
    }

    async put(uri, data) {
        this.setEnviromentUrl()
        let token = store.getters['login/getToken']
        if (!token) {
            token = await db_login.getItem('token')
        }
        axios.defaults.headers.common['Authorization'] = token
        const url = API_URL + uri
        return axios
            .put(url, data, {
                withCredentials: true,
                timeout,
                headers: { 'Content-Type': 'application/json' },
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    return status === 200
                }
            })
            .then((response) => response.data)
            .catch((error) => {
                if (error.response && error.response.status && error.response.status !== 200) {
                    return Promise.reject({ status: false, msg: 'Error: Api Error' })
                }
                return Promise.reject({ status: false, msg: 'Error: Network Error' + error })
            })
    }

    async patch(uri, data) {
        this.setEnviromentUrl()
        let token = store.getters['login/getToken']
        if (!token) {
            token = await db_login.getItem('token')
        }
        axios.defaults.headers.common['Authorization'] = token
        const url = API_URL + uri
        return axios
            .patch(url, data, {
                withCredentials: true,
                timeout,
                headers: { 'Content-Type': 'application/json' },
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    return status === 200
                }
            })
            .then((response) => response.data)
            .catch((error) => {
                if (error.response && error.response.status && error.response.status !== 200) {
                    return Promise.reject({ status: false, msg: 'Error: Api Error' })
                }
                return Promise.reject({ status: false, msg: 'Error: Network Error' + error })
            })
    }

    async delete(uri) {
        this.setEnviromentUrl()
        let token = store.getters['login/getToken']
        if (!token) {
            token = await db_login.getItem('token')
        }
        axios.defaults.headers.common['Authorization'] = token
        const url = API_URL + uri
        return axios
            .delete(url, {
                withCredentials: true,
                timeout,
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    return status === 200
                }
            })
            .then((response) => response.data)
            .catch((error) => {
                if (error.response && error.response.status && error.response.status !== 200) {
                    return Promise.reject({ status: false, msg: 'Error: Api Error' })
                }
                return Promise.reject({ status: false, msg: 'Error: Network Error' + error })
            })
    }

    login(uri, params) {
        this.setEnviromentUrl()

        const oParams = new URLSearchParams()
        Object.keys(params).forEach((key) => {
            oParams.append(key, params[key])
        })

        const url = API_URL + uri
        return axios.post(url, oParams, {}).then(function (response) {
            return response.data
        })
    }

    async getSession(params) {
        this.setEnviromentUrl()

        let token = store.getters['login/getToken']
        if (!token) {
            token = await db_login.getItem('token')
        }

        axios.defaults.headers.common['Authorization'] = token

        const url = API_URL + 'init'
        const appVersion = process.env.VUE_APP_VERSION
        const platform = process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2
        const coords = localStorage.device_coords ? localStorage.device_coords : false
        const uuid = localStorage.device_uuid ? localStorage.device_uuid : false
        const oParams = new URLSearchParams()
        const mq = typeof params.mq !== 'undefined' && params.mq ? params.mq : null
        const auditor = typeof params.auditor !== 'undefined' && params.auditor ? JSON.stringify({ ...params.auditor }) : false

        oParams.append('version', appVersion)
        oParams.append('platform', platform)
        oParams.append('mq', mq)
        oParams.append('auditor', auditor)
        oParams.append('device', params.device)

        if (uuid) oParams.append('uuid', uuid)
        if (coords) oParams.append('geolocation', JSON.parse(coords).latitude + ',' + JSON.parse(coords).longitude)

        return axios
            .post(url, oParams, {
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    return true
                }
            })
            .then(function (response) {
                return response.data
            })
            .catch((error) => {
                // TODO: validate more error messages
                return { status: false, msg: 'Error: Network Error' }
            })
    }

    async getFile(uri) {
        this.setEnviromentUrl()
        // axios.defaults.headers.common['Authorization'] = await db_login.getItem('token')
        delete axios.defaults.headers.common['Authorization']
        const url = API_URL + uri
        return axios
            .get(url, {
                withCredentials: false,
                responseType: 'blob',
                validateStatus: function (status) {
                    if ([401, 402, 403].includes(status)) {
                        window.location.href = store.getters.getIsIOS ? '/logout' : process.env.VUE_APP_IS_APP === 'TRUE' ? cordova.file.applicationDirectory + 'www/index.html#/logout' : window.window.location.origin + (window.location.hash == '' ? '' : window.location.pathname.substring(0, window.location.pathname.length - 1)) + (process.env.VUE_APP_URL_TYPE == 'HASH' ? '/#' : '') + '/logout'
                        return false
                    }
                    return true
                }
            })
            .then(function (response) {
                return response.data
            })
            .catch((error) => {
                // TODO: validate more error messages
                return { status: false, msg: 'Error: Network Error' }
            })
    }

    /**
     * URL base de la API según la configuración activa.
     *
     * @returns {string}
     */
    getBaseUrl() {
        this.setEnviromentUrl()

        return API_URL
    }

    /**
     * Plataforma actual: 1 = APP, 2 = DESKTOP.
     *
     * @returns {number}
     */
    getPlatform() {
        return process.env.VUE_APP_IS_APP == 'TRUE' ? 1 : 2
    }

    /**
     * Token de sesión desde el store o, en su defecto, desde el almacenamiento local.
     *
     * @returns {Promise<string|null>}
     */
    async getAuthToken() {
        const token = store.getters['login/getToken']
        if (token) {
            return token
        }

        return await db_login.getItem('token')
    }

    /**
     * Descarga autenticada de un fichero (blob) conservando el nombre de Content-Disposition.
     *
     * @param {string} uri
     * @param {object} [options]
     * @returns {Promise<{status: boolean, blob?: Blob, filename?: string, msg?: string}>}
     */
    async download(uri, options = {}) {
        const url = this.getBaseUrl() + uri
        axios.defaults.headers.common['Authorization'] = await this.getAuthToken()
        return axios
            .get(url, {
                withCredentials: true,
                responseType: 'blob',
                timeout: options.timeout || 60000,
                validateStatus: async function (status) {
                    if ([401, 402, 403].includes(status)) {
                        return await handleUnauthorizedSession()
                    }
                    return status === 200
                }
            })
            .then(function (response) {
                const contentType = response.headers['content-type'] || ''
                if (contentType.includes('application/json')) {
                    return { status: false, msg: 'Error: Api Error' }
                }

                return {
                    status: true,
                    blob: response.data,
                    filename: parseContentDispositionFilename(response.headers['content-disposition'])
                }
            })
            .catch(() => {
                return { status: false, msg: 'Error: Network Error' }
            })
    }

    async language(lang) {
        this.setEnviromentUrl()
        let token = store ? store.getters['login/getToken'] : null
        if (!token) {
            token = await db_login.getItem('token')
        }
        if (token) {
            axios.defaults.headers.common['Authorization'] = token
        } else {
            delete axios.defaults.headers.common['Authorization']
        }
        const url = API_URL + 'lang/' + lang
        return axios
            .get(url, {
                validateStatus: function (status) {
                    return status >= 200 && status < 300
                }
            })
            .then(function (response) {
                return response.data
            })
    }

    setEnviromentUrl() {
        if (store) {
            const config = store.getters['login/getConfig']

            if (config && config.api) {
                API_URL = config.api
            } else {
                API_URL = process.env.VUE_APP_API_URL
            }
        }
    }
}
